import {
  AppShell,
  Button,
  Flex,
  Group,
  Stack,
  Space,
} from '@mantine/core';
import {
  useElementSize,
  useHeadroom,
} from '@mantine/hooks';
import LoginIcon from '@mui/icons-material/Login';
import {
  createFileRoute,
  Link,
  Outlet,
} from '@tanstack/react-router';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  useAuth,
} from 'react-oidc-context';

import styles from '#features/landing/_landing.module.scss';
import SpeciaLogo from '#resources/images/logos/speciaLogo.svg?react';
import {
  inviteTokenValidator,
} from '#src/types/inviteToken';
import {
  resolveObject,
} from '@specia-accountant/shared';
import cookies from '#utils/Cookies';

export const Route = createFileRoute('/_app/_landing')({
  component: LandingLayout,
  validateSearch: inviteTokenValidator,
  loaderDeps: ({
    search: {
      token,
    },
  }) => ({
    token,
  }),
  loader: async (opts) => {
    const {
      deps: {
        token,
      },
      context: {
        queryClient,
      },
    } = opts;
    const status = token
      ? queryClient.ensureQueryData({
          queryKey: [
            'microsoft',
            'subscription',
            'new',
          ],
        })
      : Promise.resolve({});
    return resolveObject({
      status,
    });
  },
});

export function LandingLayout(): React.ReactNode {
  const auth = useAuth();
  const currentPath = cookies.get('currentPath') || '/';
  const {
    status,
  } = Route.useLoaderData();
  const pinnedHeader = useHeadroom({
    fixedAt: 80,
  });
  const headerLogoSize = useElementSize();

  return (
    <>
      <AppShell
        className={ styles.layoutShell }
        transitionDuration={ 1000 }
        transitionTimingFunction="ease-in-out"
        withBorder={ false }
        header={ {
          height: 80,
          collapsed: !pinnedHeader,
          offset: false,
        } }
        padding="md"
      >
        <AppShell.Header
          className={ styles.layoutHeader }
        >
          <div
            className={ styles.headerEdge }
          >
          </div>
          <Group
            h="100%"
            px="md"
            justify="space-between"
          >
            <Group
              ref={ headerLogoSize.ref }
              className={ styles.layoutHeaderLogo }
              ta="center"
              pt="10px"
            >
              <Link
                to="/"
                aria-label="Applicaition icon home link"
              >
                <SpeciaLogo
                  width="60px"
                  height="60px"
                />
              </Link>
              <Space
                w="xl"
              />
            </Group>
            <Flex
              className={ styles.layoutLinks }
            >
              <Button
                variant="subtle"
                color="gray"
                component={ Link }
                to="/"
              >
                Home
              </Button>
              { auth.isAuthenticated
                && (
                  <Button
                    variant="subtle"
                    color="gray"
                    component={ Link }
                    to="/mydigitalworkers"
                  >
                    My Digital Workers
                  </Button>
                ) }
              <Button
                variant="subtle"
                color="gray"
                component={ Link }
                to="/solutions"
              >
                Solutions
              </Button>
              <Button
                variant="subtle"
                color="gray"
                component={ Link }
                to="/pricing"
              >
                Pricing
              </Button>
              <Button
                variant="subtle"
                color="gray"
                component={ Link }
                to="/contact"
              >
                Contact
              </Button>
            </Flex>
            <Group
              pr="md"
              justify="flex-end"
              ta="center"
              py="xs"
              // w={ headerLogoSize.width }
            >
              { auth.isAuthenticated
                ? (
                    <Button
                      onClick={ () => {
                        auth.signoutRedirect();
                      } }
                    >
                      Sign Out
                    </Button>
                  )
                : (
                    <Button
                      onClick={ () => {
                        auth.signinRedirect();
                      } }
                    >
                      Sign In
                      <Space
                        w="sm"
                      />
                      <LoginIcon />
                    </Button>
                  ) }
            </Group>
          </Group>
        </AppShell.Header>
        <AppShell.Main
          id="app-shell-main"
          className={ styles.layoutMain }
        >
          <Outlet />
          <footer
            className={ styles.layoutFooter }
          >
            <div
              className={ styles.layoutFooterLinks }
            >
              <Button
                variant="subtle"
                color="gray"
                size="md"
                component={ Link }
                to="/solutions"
                activeOptions={ {
                  includeSearch: false,
                } }
                activeProps={ {
                  className: 'font-bold',
                } }
              >
                Solutions
              </Button>
              <Button
                variant="subtle"
                color="gray"
                size="md"
                component={ Link }
                to="/pricing"
                activeOptions={ {
                  includeSearch: false,
                } }
                activeProps={ {
                  className: 'font-bold',
                } }
              >
                Pricing
              </Button>
              <Button
                variant="subtle"
                color="gray"
                size="md"
                component={ Link }
                to="/contact"
                activeOptions={ {
                  includeSearch: false,
                } }
                activeProps={ {
                  className: 'font-bold',
                } }
              >
                Contact Us
              </Button>
            </div>

            <div
              style={ {
                marginBottom: '3vw',
                color: 'rgb(255, 255, 255, 0.7)',
              } }
            >
              { ' ' }
              © 2024 Specia | All Rights Reserved |
              { ' ' }
              <Link
                to="/privacy"
              >
                Privacy Policy
              </Link>
              { ' ' }
            </div>

            <div
              style={ {
                height: '12vw',
                overflow: 'hidden',
              } }
              className="huge-circle"
            >
              <SpeciaLogo
                width="30vw"
              />
            </div>
          </footer>
        </AppShell.Main>
      </AppShell>
    </>
  );
}
